import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { INVOICE_DRAFTS_QUERY_KEY } from '@/features/invoices/compose/api';
import { OUTBOUND_QUERY_KEY } from '@/features/invoices/outbound/api';
import { useBadgeCounts, type BadgeCounts } from './api';

/**
 * Turns the existing badge-count poll into a "something changed" heartbeat for the write-side lists.
 *
 * `/api/v1/badge-counts` already polls app-wide (~10s while anything is in-flight, ~60s once settled) and
 * carries the exact per-state outbound vector plus the started-draft count. That was only painting sidebar
 * pills; here it also drives a silent refetch of whichever list the user is looking at, so a draft created
 * in another tab or a `QUEUED → AWAITING_KSEF → COMPLETED` transition appears on its own.
 *
 * Deliberately a *client-side* change signal — no server change token, no extra request, no cadence
 * change.
 *
 * Known blind spot, accepted: a change that moves no count (a draft edited, not created, in another tab)
 * does not fire. Closing that needs a server-side `max(updated_at)` token; deferred.
 */

/** Order-independent snapshot of one badge payload — exported for tests. */
export function fingerprint(counts: BadgeCounts): { drafts: string; outbound: string } {
  // Zero counts are omitted so an absent key and an explicit 0 hash alike (the API zero-fills every
  // state, but a payload that didn't would otherwise look like a change). A count moving to or from zero
  // still changes the string, so nothing real is missed.
  const outbound = Object.entries(counts.outbound ?? {})
    .filter(([, count]) => (count ?? 0) > 0)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([state, count]) => `${state}:${count}`)
    .join(',');
  return { drafts: String(counts.drafts ?? 0), outbound };
}

/**
 * Watches the badge payload and invalidates the list whose count moved — drafts and outbound compared
 * independently, so a draft appearing never refetches the outbound register.
 *
 * Fingerprints the *entire* outbound map, not just the three groups `NavBadges` paints: terminal states
 * matter here, since `ACCEPTED → COMPLETED` is exactly the kind of transition that should move the
 * Outgoing list.
 *
 * Silent by design — it only invalidates, so TanStack refetches every loaded page in place and the user's
 * scroll position and page depth hold (unlike the manual button, which collapses to page 1). Inactive
 * lists are merely marked stale, so an unmounted list costs nothing and is fresh on the next visit.
 *
 * Mount once, app-wide (see `AppShell`), gated on `me.issuingEnabled` — the endpoint 404s when issuing
 * is off.
 */
export function useBadgeCountRefresh(): void {
  const queryClient = useQueryClient();
  const { data } = useBadgeCounts();
  const previous = useRef<{ drafts: string; outbound: string } | null>(null);

  useEffect(() => {
    if (!data) return;
    const next = fingerprint(data);
    const before = previous.current;
    previous.current = next;
    // First payload is the baseline, not a change — an initial load must not trigger a second fetch.
    if (!before) return;
    if (before.drafts !== next.drafts) {
      void queryClient.invalidateQueries({ queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'list'] });
    }
    if (before.outbound !== next.outbound) {
      void queryClient.invalidateQueries({ queryKey: [...OUTBOUND_QUERY_KEY, 'list'] });
    }
  }, [data, queryClient]);
}
